
import React, { useState } from "react";
import navigate from "@helpers/link-navigate";
import useToggle from "hooks/useVehicleStylesToggle";
import Image from "components/image";
import getModulePadding from "helpers/get-module-padding";
import Link from "helpers/link";
import SrOnly from "../sr-only";
import CommonDropdown from "components/dropdowns/common-dropdown";
import ModalDropdown from "components/dropdowns/modal-dropdown";
import NProgress from "nprogress";

const VehicleLinks = ({
    style,
    isMobile,
    isOpen,
    stylesPerRowSettings,
    itemIndex,
    selectedLinkTitle
}) => {
    if (!style?.links) {
        return <div />;
    }

    const containerClassName = isMobile
        ? "md:hidden flex flex-col ml-4 mt-4 z-0"
        : "vehicle-style-links z-0";

    return (
        <div className={containerClassName}>
            <p className="text-gray-701 text-xs font-semibold">{style?.linksTitle}</p>
            <SrOnly label={`${selectedLinkTitle || style?.linksTitle} dropdown`} />
            <div className="flex flex-col">
                {style?.links?.map((link) => (
                    <Link
                        key={link?.title}
                        to={link?.url}
                        className={`${itemIndex > stylesPerRowSettings.hiddenLinksElementStyling
                            ? isOpen
                                ? ""
                                : "pointer-events-none"
                            : ""
                            } relative text-gray-701 text-xs font-semibold my-1 pl-4 z-50 before:content-[''] before:absolute before:top-1.5 before:left-0 before:w-1 before:h-1 before:bg-gray-701 before:rounded-1/2`}
                    >
                        {link?.title}
                    </Link>
                ))}
            </div>
        </div>
    );
};

const DesktopVehicleLinks = ({
    style,
    isOpen,
    stylesPerRowSettings,
    itemIndex,
    selectedLinkTitle,
    setSelectedLinkTitle,
}) => (
    <VehicleLinks
        style={style}
        isMobile={false}
        isOpen={isOpen}
        stylesPerRowSettings={stylesPerRowSettings}
        itemIndex={itemIndex}
        selectedLinkTitle={selectedLinkTitle}
        setSelectedLinkTitle={setSelectedLinkTitle}
    />
);

const MobileVehicleLinks = ({
    style,
    isOpen,
    stylesPerRowSettings,
    itemIndex,
    selectedLinkTitle,
    setSelectedLinkTitle,
}) => (
    <VehicleLinks
        style={style}
        isMobile
        isOpen={isOpen}
        stylesPerRowSettings={stylesPerRowSettings}
        itemIndex={itemIndex}
        selectedLinkTitle={selectedLinkTitle}
        setSelectedLinkTitle={setSelectedLinkTitle}
    />
);

const VehicleStylesShowcase = ({
    data: { title, stylesPerRow, styles, useDropdowns },
}) => {
    const { isOpen, toggle } = useToggle();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [selectedLinkTitle, setSelectedLinkTitle] = useState(null);
    const [openDropdownIndex, setOpenDropdownIndex] = useState(null);

    const toggleDropdown = (index) => {
        setOpenDropdownIndex(openDropdownIndex === index ? null : index);
    };



    const stylesPerRowSettings = {
        width: stylesPerRow === 4 ? "xl:w-[23.8%]" : "xl:w-[32%]",
        showSeeMoreButton: stylesPerRow === 4 ? 8 : 6,
        hiddenLinksElementStyling: stylesPerRow === 4 ? 9 : 5,
        extraBottomMargin: stylesPerRow === 3 && "md:mt-14",
    };

    const handleSelectLink = (property, selectedValue, links) => {
        if (selectedValue) {
            let selectedItem = links.find((value) => value.title === selectedValue);
            if (selectedItem) {
                setSelectedLinkTitle(selectedValue);
                NProgress.start();
                navigate(selectedItem.url);
            }
        }
    };

    return (
        <div className="mx-auto container pb-4">
            <div className="bg-white">
                <h3 className="text-blue-700 font-medium md:mb-12 mb-4 text-center md:text-3xl">
                    {title}
                    <SrOnly label={title} />
                </h3>
                <div
                    className={`relative transition-all duration-500 ease-in-out rounded-sm ${getModulePadding()}`}
                >
                    <div
                        className={`flex flex-wrap ${styles?.length >= 6 ? "pb-4" : ""} ${styles?.length < 3 ? "justify-center" : ""
                            }`}
                    >
                        {styles.map((style, index) => (
                            <div
                                tabIndex={0}
                                role="button"
                                aria-label={`${style?.title} ${style?.subtitle}`}
                                key={style?._key}
                                className={`${stylesPerRowSettings.width
                                    } md:w-[48%] w-full h-auto justify-between relative group transition-all duration-500 ease-in-out flex flex-col ${index > stylesPerRowSettings.hiddenLinksElementStyling
                                        ? isOpen
                                            ? "max-h-full px-2 pt-1.5 pb-3 m-[0.4rem] hover:cursor-pointer"
                                            : "p-0 opacity-0 max-h-0 m-0 hover:cursor-default"
                                        : "px-2 pt-1.5 pb-3 m-[0.4rem] hover:cursor-pointer "
                                    } shadow-accessories rounded-xl ${style?.links?.length > 0 && "hover:rounded-b-none"
                                    }`}
                            >

                                {useDropdowns ? (
                                    <div className={`flex flex-row gap-1 items-center justify-center text-blue-700 font-medium mb-2 p-5`}>
                                        {style?.image?.url ? (
                                            <Link to={style?.image?.url} className="font-medium">
                                                {style?.title}
                                            </Link>
                                        ) : (
                                            <p className="font-medium text-center">{style?.title}</p>
                                        )}
                                        {style?.subtitle && (
                                            <p className="font-medium">{style?.subtitle}</p>
                                        )}
                                    </div>
                                ) : null}


                                {style?.image?.url ? (
                                    <Link to={style?.image?.url} className="z-0" tabIndex={-1}>
                                        <Image
                                            image={style?.image?.asset}
                                            className="z-0"
                                            tabIndex={-1}
                                        />
                                    </Link>
                                ) : (
                                    <div className="z-0">
                                        <Image image={style?.image?.asset} className="z-0" />
                                    </div>
                                )}

                                {useDropdowns ? null : (
                                    <div className={`flex flex-row gap-1 items-center justify-center text-blue-700 font-medium mb-2`}>
                                        {style?.image?.url ? (
                                            <Link to={style?.image?.url} className="font-medium text-center px-4">
                                                {style?.title}
                                            </Link>
                                        ) : (
                                            <p className="font-medium text-center px-4">{style?.title}</p>
                                        )}
                                        {style?.subtitle && (
                                            <p className="font-medium">{style?.subtitle}</p>
                                        )}
                                    </div>
                                )}


                                {style?.links?.length > 0 && (
                                    <div className="shop-by-style__dropdown flex justify-center lg:flex-row flex-col lg:gap-5 gap-5">
                                        <SrOnly label={`style?.linksTitle} dropdown`} />
                                        {useDropdowns ? (
                                            <>
                                                <div className="hidden grow lg:block">
                                                    <CommonDropdown
                                                        selected={style?.linksTitle}
                                                        options={style.links}
                                                        optionProperty="title"
                                                        onSelect={(property, selectedValue) =>
                                                            handleSelectLink(
                                                                property,
                                                                selectedValue,
                                                                style.links
                                                            )
                                                        }
                                                        hasSearch
                                                        disabled={false}
                                                        className="w-full relative"
                                                        key={`links-dropdown-${index}`}
                                                    />
                                                </div>
                                                <div className="block lg:hidden">
                                                    <ModalDropdown
                                                        selected={style?.linksTitle}
                                                        options={style.links}
                                                        optionProperty="title"
                                                        onSelect={(property, selectedValue) =>
                                                            handleSelectLink(
                                                                property,
                                                                selectedValue,
                                                                style.links
                                                            )
                                                        }
                                                        hasSearch
                                                        disabled={false}
                                                        isOpen={openDropdownIndex === index}
                                                        toggle={() => toggleDropdown(index)}
                                                        key={`links-modal-dropdown-${index}`}
                                                        onOpen={() => setOpenDropdownIndex(index)}
                                                        onGoBack={() => setOpenDropdownIndex(null)}
                                                        showModal={openDropdownIndex === index}
                                                        className="lg:hidden flex"
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    disabled={!selectedLinkTitle}
                                                    className="transition-all duration-300 font-normal md:text-base text-base-xs flex hover:text-white hover:bg-orange-800 focus:bg-orange-800 focus:text-white bg-orange-703 lg:w-20 w-full lg:h-11 h-8 rounded-lg items-center justify-center"
                                                    onClick={() =>
                                                        handleSelectLink(
                                                            null,
                                                            style.links[0].title,
                                                            style.links
                                                        )
                                                    }
                                                >
                                                    Go
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <DesktopVehicleLinks
                                                    style={style}
                                                    isOpen={isOpen}
                                                    stylesPerRowSettings={stylesPerRowSettings}
                                                    itemIndex={index}
                                                    selectedLinkTitle={selectedLinkTitle}
                                                    setSelectedLinkTitle={setSelectedLinkTitle}
                                                />
                                                <MobileVehicleLinks
                                                    style={style}
                                                    isOpen={isOpen}
                                                    stylesPerRowSettings={stylesPerRowSettings}
                                                    itemIndex={index}
                                                    selectedLinkTitle={selectedLinkTitle}
                                                    setSelectedLinkTitle={setSelectedLinkTitle}
                                                />
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    {styles?.length > stylesPerRowSettings.showSeeMoreButton && (
                        <div
                            className={`relative z-10 w-full transition-opacity duration-500 ease-in-out flex justify-center ${isOpen ? "hidden" : ""
                                }`}
                        >
                            <button
                                type="button"
                                className="transition-[background] duration-300 ease-in-out font-bold rounded-sm md:text-base text-base-xs bg-[#646a63] text-white md:px-12 md:py-2 px-9 py-1.5 hover:bg-[#303030]"
                                onClick={toggle}
                                onKeyDown={toggle}
                            >
                                See More
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VehicleStylesShowcase;
